import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  MapPin,
  ShieldCheck,
  Building2,
  Factory,
  Truck,
  Plane,
  Network,
  Zap,
  Phone,
  Clock,
  BadgeCheck,
  Wrench,
  HelpCircle,
} from "lucide-react";
import { PlatformShellHeader, PlatformShellFooter, useSeo } from "./PlatformShell";
import { getCityProfile, CURATED, PROGRAMMATIC } from "../data/cityCatalogue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://www.boombarriercompany.com";

const USE_ICONS = [Building2, Factory, Truck, Plane, Network, Zap];

export default function CityMetro({ slug: slugProp }) {
  const params = useParams();
  const slug = String(slugProp || params.city || "").toLowerCase();
  const profile = getCityProfile(slug);

  const [apiData, setApiData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let mounted = true;
    setStatus("loading");
    axios
      .get(`${API}/cities/${slug}`)
      .then((r) => {
        if (!mounted) return;
        setApiData(r.data);
        setStatus("ok");
      })
      .catch(() => {
        if (mounted) setStatus(profile ? "ok" : "missing");
      });
    return () => {
      mounted = false;
    };
  }, [slug, profile]);

  const cityName = apiData?.name || (profile ? profile.slug.charAt(0).toUpperCase() + profile.slug.slice(1) : "");
  const stateName = apiData?.state || "";
  const responseSla = apiData?.response_sla || "≤ 1 business day";
  const canonical = `${SITE}/boom-barrier-${slug}`;

  useSeo(
    profile
      ? {
          title: `${profile.keyword} — Automatic Boom Barrier · RFID · FASTag · ANPR | Boom Barrier Company`,
          description: `${profile.tagline} BIS-tested, QR-traceable, factory-warranted. Site-specific quotes in ≤ 1 business day. Service SLA: ${responseSla}.`,
          canonical,
          image: `${SITE}/branded/hero-signature.png`,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              name: `Boom Barrier Company — ${cityName}`,
              url: canonical,
              image: `${SITE}/branded/hero-signature.png`,
              telephone: "+91-87963-83008",
              areaServed: { "@type": "City", name: cityName },
              address: {
                "@type": "PostalAddress",
                addressLocality: cityName,
                ...(stateName ? { addressRegion: stateName } : {}),
                addressCountry: "IN",
              },
              priceRange: "₹₹",
              description: profile.tagline,
              openingHoursSpecification: {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                opens: "09:30",
                closes: "18:30",
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: profile.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: profile.keyword, item: canonical },
              ],
            },
          ],
        }
      : { title: "Boom Barrier Company" }
  );

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#0F1113] text-white">
        <PlatformShellHeader />
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-24" data-testid="city-metro-missing">
          <h1 className="font-display text-white text-4xl font-black tracking-tight">
            We don't have a metro page for this city yet.
          </h1>
          <p className="mt-4 text-[#A1A1AA]">
            Try <Link to="/boom-barrier-delhi" className="text-[#00FF85] underline">Delhi</Link>,{" "}
            <Link to="/boom-barrier-mumbai" className="text-[#00FF85] underline">Mumbai</Link>, or{" "}
            <Link to="/boom-barrier-bengaluru" className="text-[#00FF85] underline">Bengaluru</Link>.
          </p>
        </div>
        <PlatformShellFooter />
      </div>
    );
  }

  const useCases = apiData?.use_cases || profile.deploymentTypes;

  return (
    <div data-testid="city-metro-root" className="min-h-screen bg-[#0F1113] text-white font-body antialiased">
      <PlatformShellHeader />

      {/* HERO */}
      <section className="relative pt-16 md:pt-24 pb-14 border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 bbc-grid-bg opacity-[0.06] pointer-events-none" />
        <div
          className="absolute -top-32 -right-32 w-[700px] h-[700px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,255,133,0.08), transparent 60%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#71717A]">
            <Link to="/" className="hover:text-[#00FF85]">Home</Link>
            <span>/</span>
            <span className="text-[#A1A1AA]">{profile.keyword}</span>
          </nav>
          <div className="mt-6 flex items-center gap-3">
            <MapPin size={14} className="text-[#00FF85]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85]">
              {cityName} · India
            </span>
          </div>
          <h1 className="font-display mt-4 text-white text-4xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[0.98] max-w-5xl">
            {profile.keyword}
            <br />
            <span className="text-[#00FF85]">Built like infrastructure.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed">
            {profile.tagline}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/quote"
              data-testid={`metro-${slug}-cta-quote`}
              className="inline-flex items-center gap-3 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-6 py-3.5 text-xs uppercase tracking-wider shadow-[0_0_24px_rgba(0,255,133,0.35)] transition"
            >
              Get a {cityName} Quote <ArrowRight size={14} />
            </Link>
            <a
              href="tel:+918796383008"
              data-testid={`metro-${slug}-cta-call`}
              className="inline-flex items-center gap-3 border border-white/15 hover:border-white text-white font-semibold px-6 py-3.5 text-xs uppercase tracking-wider transition"
            >
              <Phone size={14} /> Call BBC Sales
            </a>
          </div>

          {/* Localities strip */}
          <div className="mt-10 flex flex-wrap gap-2" data-testid={`metro-${slug}-localities`}>
            {profile.localities.map((l) => (
              <span
                key={l}
                className="inline-flex items-center gap-1.5 border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]"
              >
                <MapPin size={10} className="text-[#00FF85]" />
                {l}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="py-10 border-b border-white/5 bg-[#13161A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid sm:grid-cols-3 gap-px bg-white/5">
          {[
            { icon: BadgeCheck, kicker: "Install base", value: profile.installBase },
            { icon: Truck, kicker: "Delivery", value: profile.delivery },
            { icon: Clock, kicker: "Response SLA", value: responseSla },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-[#16191C] p-6">
                <Icon size={14} className="text-[#00FF85]" />
                <div className="mt-3 font-mono text-[9px] uppercase tracking-[0.22em] text-[#71717A]">{s.kicker}</div>
                <div className="mt-1 text-white text-base md:text-lg font-body">{s.value}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* MODELS */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-white text-3xl md:text-4xl font-black tracking-[-0.02em]">
            Boom barrier models available in {cityName}
          </h2>
          <div className="mt-10 grid md:grid-cols-4 gap-3">
            {[
              { name: "PRIME 100W", role: "Residential · Society", to: "/products/prime", img: "/branded/bbc-cab-classic-green.png" },
              { name: "ELITE 150W", role: "Commercial · Toll", to: "/products/elite", img: "/branded/bbc-cab-led-branded-green.png" },
              { name: "FLASH 180W", role: "Sub-second · Corporate", to: "/products/flash", img: "/branded/bbc-cab-video-display.png" },
              { name: "MAX Industrial", role: "Heavy-duty · Fence arm", to: "/products/max", img: "/branded/bbc-max-orange-full.png" },
            ].map((m) => (
              <Link
                key={m.name}
                to={m.to}
                data-testid={`metro-${slug}-model-${m.name.split(" ")[0].toLowerCase()}`}
                className="group border border-white/10 hover:border-[#00FF85]/50 bg-[#16191C] overflow-hidden transition"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-br from-[#13161A] to-black">
                  <img
                    src={m.img}
                    alt={`${m.name} automatic boom barrier in ${cityName}`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-contain p-6 opacity-90 group-hover:opacity-100 transition"
                  />
                </div>
                <div className="p-5 border-t border-white/5">
                  <h3 className="font-display text-white text-lg font-bold tracking-tight">{m.name}</h3>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">{m.role}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-16 md:py-20 border-b border-white/5 bg-[#13161A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-white text-3xl md:text-4xl font-black tracking-[-0.02em]">
            Where BBC barriers are running in {cityName}
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {useCases.map((u, i) => {
              const Icon = USE_ICONS[i % USE_ICONS.length];
              return (
                <div key={i} className="bg-[#16191C] p-6">
                  <div className="w-10 h-10 border border-white/10 flex items-center justify-center">
                    <Icon size={16} className="text-[#00FF85]" />
                  </div>
                  <p className="mt-4 text-white text-sm font-body leading-relaxed">{u}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs (schema-bound) */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="inline-flex items-center gap-2 border border-[#00FF85]/40 bg-[#00FF85]/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">
            <HelpCircle size={12} /> FAQs · {cityName}
          </div>
          <h2 className="font-display mt-4 text-white text-3xl md:text-4xl font-black tracking-[-0.02em]">
            Questions we get from buyers in {cityName}
          </h2>
          <div className="mt-10 divide-y divide-white/5 border-y border-white/5" data-testid={`metro-${slug}-faqs`}>
            {profile.faqs.map((f, i) => (
              <details key={i} className="group py-5">
                <summary className="cursor-pointer flex items-start justify-between gap-6 text-white font-display font-bold text-base md:text-lg tracking-tight list-none">
                  <span>{f.q}</span>
                  <span className="mt-1 font-mono text-[10px] text-[#00FF85] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#A1A1AA] text-sm md:text-base leading-relaxed font-body">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 border border-[#00FF85]/40 bg-[#00FF85]/10 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00FF85]">
            <Wrench size={12} /> {profile.installBase}
          </div>
          <h2 className="font-display mt-6 text-white text-3xl md:text-5xl font-black tracking-[-0.02em]">
            Ready to install a BBC barrier in {cityName}?
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-2xl mx-auto">
            Share your site, footfall and timeline. Our {cityName} team will spec the right BBC model and reach out within one business day.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/quote"
              data-testid={`metro-${slug}-cta-quote-bottom`}
              className="inline-flex items-center gap-3 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-7 py-4 text-xs uppercase tracking-wider shadow-[0_0_28px_rgba(0,255,133,0.4)] transition"
            >
              Talk to BBC {cityName} <ArrowRight size={14} />
            </Link>
            <Link
              to="/products/compare"
              className="inline-flex items-center gap-3 border border-white/15 hover:border-white text-white font-semibold px-7 py-4 text-xs uppercase tracking-wider transition"
            >
              Compare Models
            </Link>
          </div>
        </div>
      </section>

      {/* Cross-links to sibling metros — helps crawl + human nav */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Also serving</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(() => {
              const curated = Object.values(CURATED)
                .filter((p) => p.slug !== slug)
                .slice(0, 6);
              const sameRegion = PROGRAMMATIC
                .filter((p) => p.region === profile.region && p.slug !== slug)
                .slice(0, 6);
              const seen = new Set();
              const siblings = [...curated, ...sameRegion].filter((p) => {
                if (seen.has(p.slug)) return false;
                seen.add(p.slug);
                return true;
              }).slice(0, 10);
              return siblings.map((p) => (
                <Link
                  key={p.slug}
                  to={`/boom-barrier-${p.slug}`}
                  className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[#00FF85] hover:text-[#00FF85] text-white/80 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition"
                >
                  <MapPin size={10} />
                  Boom Barrier in {p.name}
                </Link>
              ));
            })()}
          </div>
        </div>
      </section>

      <PlatformShellFooter />
    </div>
  );
}
